import React, { useRef, useEffect, useCallback, useState } from 'react';

interface DraggablePopularTagProps {
  label: string;
  className?: string; // e.g. "top-5 right-8"
}

export default function DraggablePopularTag({ label, className = '' }: DraggablePopularTagProps) {
  const [isDragging, setIsDragging] = useState(false);
  const svgRef = useRef<SVGSVGElement>(null);
  const pinRef = useRef<HTMLDivElement>(null);
  const lastDragAngle = useRef(0);
  const animationRef = useRef<number>();
  const autoSwingTime = useRef(0);
  const isDraggingRef = useRef(false);
  const rotationRef = useRef(0);
  const velocityRef = useRef(0);
  const isAutoSwingingRef = useRef(true);

  const applyRotation = (deg: number) => {
    rotationRef.current = deg;
    if (svgRef.current) {
      svgRef.current.style.transform = `rotate(${deg}deg)`;
    }
  };

  const handleGlobalMouseMove = useCallback((e: MouseEvent) => {
    if (!isDraggingRef.current || !pinRef.current) return;

    // 获取钉子中心在屏幕上的绝对坐标
    const pinRect = pinRef.current.getBoundingClientRect();
    const pinX = pinRect.left + pinRect.width / 2;
    const pinY = pinRect.top + pinRect.height / 2;

    // 计算鼠标相对于钉子的角度
    const dx = e.clientX - pinX;
    const dy = e.clientY - pinY;

    // 计算角度 (0度为正下方)
    let angle = (Math.atan2(dy, dx) * 180) / Math.PI;
    angle = angle - 90;

    // 归一化到 [-180, 180] 之间
    if (angle > 180) angle -= 360;
    if (angle < -180) angle += 360;

    // 限定最大拖拽角度，防止转圈圈
    const targetRotation = Math.max(-60, Math.min(60, angle));

    // 记录拖放前一刻的角速度用于弹性释放
    velocityRef.current = (targetRotation - lastDragAngle.current) * 0.5;
    lastDragAngle.current = targetRotation;

    applyRotation(targetRotation);
  }, []);

  const handleGlobalMouseUp = useCallback(() => {
    isDraggingRef.current = false;
    setIsDragging(false);
    isAutoSwingingRef.current = false;
    document.removeEventListener('mousemove', handleGlobalMouseMove);
    document.removeEventListener('mouseup', handleGlobalMouseUp);
  }, [handleGlobalMouseMove]);

  useEffect(() => {
    return () => {
      document.removeEventListener('mousemove', handleGlobalMouseMove);
      document.removeEventListener('mouseup', handleGlobalMouseUp);
    };
  }, [handleGlobalMouseMove, handleGlobalMouseUp]);

  useEffect(() => {
    const animate = () => {
      if (!isDraggingRef.current) {
        if (isAutoSwingingRef.current) {
          autoSwingTime.current += 0.02;
          applyRotation(Math.sin(autoSwingTime.current * 1.5) * 4 + 3);
        } else {
          const targetDeg = 3;
          const springForce = -(rotationRef.current - targetDeg) * 0.03;
          const dampingForce = -velocityRef.current * 0.05;
          const newVelocity = velocityRef.current + springForce + dampingForce;
          const newRotation = rotationRef.current + newVelocity;

          if (Math.abs(newRotation - targetDeg) < 0.1 && Math.abs(newVelocity) < 0.1) {
            velocityRef.current = 0;
            isAutoSwingingRef.current = true;
            applyRotation(targetDeg);
          } else {
            velocityRef.current = newVelocity;
            applyRotation(newRotation);
          }
        }
      }
      animationRef.current = requestAnimationFrame(animate);
    };

    animationRef.current = requestAnimationFrame(animate);
    return () => {
      if (animationRef.current) cancelAnimationFrame(animationRef.current);
    };
  }, []);

  const handleMouseDown = (e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    isDraggingRef.current = true;
    setIsDragging(true);
    isAutoSwingingRef.current = false;
    lastDragAngle.current = rotationRef.current;
    velocityRef.current = 0;
    document.addEventListener('mousemove', handleGlobalMouseMove);
    document.addEventListener('mouseup', handleGlobalMouseUp);
  };

  return (
    <div
      className={`absolute z-50 cursor-grab active:cursor-grabbing select-none ${className}`}
      onMouseDown={handleMouseDown}
    >
      {/* 
        1比1 像素缩放：SVG 宽度 w-32 (128px), viewbox 设置为 128。
        使得 1 SVG 单位 = 1 物理像素。
      */}
      <svg
        ref={svgRef}
        className="absolute top-0 w-32 h-44 overflow-visible pointer-events-none"
        viewBox="0 0 128 176"
        style={{
          left: '-57px', // (128 - 14) / 2 = 57. 使钉子(14px)居于 128px 宽度的中心
          transformOrigin: '64px 7px', // 旋转中心：x=64 (128/2), y=7 (钉子中心)
          transform: 'rotate(3deg)',
        }}
      >
        {/* 绳索：顶点 M 64,7 严格对齐钉子中心 */}
        <path
          d="M 64,7 L 22,44 M 64,7 L 106,44"
          fill="none"
          stroke="#a09489"
          strokeWidth="1.5"
          strokeLinecap="round"
          strokeOpacity="0.8"
        />

        {/* 标签卡片：宽度恢复为原设计的更宽尺寸 (126px) */}
        <foreignObject x="1" y="40" width="126" height="65" className="overflow-visible pointer-events-auto">
          <div className={`relative w-[126px] px-3 py-4 rounded-lg shadow-[0_8px_16px_-6px_rgba(0,0,0,0.15)] border border-surface-border overflow-hidden bg-surface-light ${isDragging ? 'ring-2 ring-brand-500/30' : ''}`}>
            <div className="absolute inset-0 bg-noise opacity-[0.05] pointer-events-none"></div>

            {/* 穿孔结构 */}
            <div className="absolute top-2 left-[21px] w-2 h-2 rounded-full bg-[#f2ecdf] shadow-[inset_0_1px_2px_rgba(0,0,0,0.1)] border border-[#a09489]/30 pointer-events-none"></div>
            <div className="absolute top-2 right-[21px] w-2 h-2 rounded-full bg-[#f2ecdf] shadow-[inset_0_1px_2px_rgba(0,0,0,0.1)] border border-[#a09489]/30 pointer-events-none"></div>

            {/* 装饰性中心孔 */}
            <div className="absolute top-1.5 left-1/2 -translate-x-1/2 w-3 h-3 rounded-full bg-surface shadow-[inset_0_1px_2px_rgba(0,0,0,0.08)] border border-surface-border flex items-center justify-center pointer-events-none">
              <div className="w-1 h-1 rounded-full bg-brand-900/10"></div>
            </div>

            <div className="mt-2.5 flex flex-col items-center">
              {/* 恢复并适当增大字号 */}
              <span className="text-brand-700 font-serif italic text-base font-black leading-none tracking-tight whitespace-nowrap">{label}</span>
              <div className="mt-1.5 w-6 h-[1.5px] bg-brand-700/15"></div>
            </div>
          </div>
        </foreignObject>
      </svg>

      {/* 钉子 (Tack)：在 DOM 中后于 SVG 渲染，并在布局上严格覆盖绳索交点 */}
      <div
        ref={pinRef}
        className="relative w-3.5 h-3.5 rounded-full bg-gradient-to-br from-gray-300 via-gray-400 to-gray-600 shadow-[0_2px_4px_rgba(0,0,0,0.3),inset_0_1px_1px_rgba(255,255,255,0.8)] border border-gray-700/20 z-10 pointer-events-none"
      >
        <div className="absolute top-0.5 left-0.5 w-0.5 h-0.5 bg-white/40 rounded-full blur-[0.5px]"></div>
      </div>
    </div>
  );
}